import { Action } from 'redux';
import { AsyncStateNotification } from '@ui/notifications';
import { AsyncStateSelector } from '@lib/selectors';
import { FeatureFlag } from '@sonar-software/sonar-core';
import React from 'react';

import { Button, ButtonProps } from 'antd';
import { COPY_GENERIC, COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { useAction, useCurrentUser, useFeatureFlag } from '@hooks';

/**
 * Generic, reusable export button for triggering async export actions with notification feedback.
 *
 * @template T - The type of the export payload/request.
 */
export interface ExportButtonProps<T> {
  /**
   * The payload to send to the export action.
   */
  request: T;
  /**
   * The function to call to start the export (usually from coreActions). Receives the payload.
   */
  onStartExport: (payload: T) => Action;
  /**
   * The function to call to reset the export state (usually from useAction or coreActions.reset...).
   */
  resetExport: () => Action;
  /**
   * Selector for the async export state (usually from createAsyncSelectors(...).state).
   */
  stateSelector: AsyncStateSelector<unknown>;
  /**
   * Whether the button should be disabled (e.g., if user has FeatureFlag.Export).
   */
  disabled?: boolean;
  /**
   * Additional props to pass to the Ant Design Button.
   */
  buttonProps?: ButtonProps;
}


export const ExportButton = <T, >({
	request,
	onStartExport,
	resetExport,
	stateSelector,
	disabled,
	buttonProps
}: ExportButtonProps<T>) => {
	const startExport = useAction(onStartExport);
	const isExportEnabled = useFeatureFlag(FeatureFlag.Export);
	const { isReadOnly } = useCurrentUser();

	if (isReadOnly) {
		return null;
	}

	return (
		<>
			<Button
				disabled={ !isExportEnabled || disabled }
				onClick={ () => startExport(request) }
				{ ...buttonProps }>
				{ COPY_SPECIFIC.EXPORT }
			</Button>
			<AsyncStateNotification
				errored={ COPY_GENERIC.CONTACT_SUPPORT }
				failed={ {
					description: COPY_GENERIC.TOO_MANY_EXPORTS,
					options: { message: COPY_GENERIC.COULD_NOT_START_EXPORT }
				} }
				received={ {
					description: COPY_GENERIC.WHEN_EXPORT_COMPLETES,
					options: { message: COPY_GENERIC.EXPORT_STARTED }
				} }
				resetRawAction={ resetExport }
				stateSelector={ stateSelector } />
		</>
	);
};
